import type {ScorecardWorkstreamCardOut, ScorecardWorkstreamUpdateOut} from 'client';
import {useCallback, useEffect, useRef, useState} from 'react';

type Save = (updateId: number | null, comment: string) => Promise<ScorecardWorkstreamUpdateOut>;

/** One serialized save queue per workstream/period, retained through navigation. */
export const useScorecardWorkstreamAutosave = (card: ScorecardWorkstreamCardOut, save: Save) => {
  const initial = card.update?.comment ?? '';
  const [draft, setDraft] = useState(initial);
  const [status, setStatus] = useState<'idle' | 'saving' | 'error'>('idle');
  const saveRef = useRef(save);
  saveRef.current = save;
  const state = useRef({
    draft: initial,
    persisted: initial,
    updateId: card.update?.id ?? null,
    meaningful: false,
    dirty: false,
    pending: null as Promise<void> | null,
  });
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const mounted = useRef(true);

  const flush = useCallback((): Promise<void> => {
    clearTimeout(timer.current);
    const current = state.current;
    if (current.pending) return current.pending;
    if (!current.dirty || (!current.updateId && !current.meaningful)) return Promise.resolve();
    // Defer so pending is assigned before the loop can finish synchronously.
    current.pending = Promise.resolve().then(async () => {
      try {
        while (current.dirty && (current.updateId !== null || current.meaningful)) {
          const comment = current.draft;
          if (mounted.current) setStatus('saving');
          const update = await saveRef.current(current.updateId, comment);
          current.updateId = update.id;
          current.persisted = comment;
          current.dirty = current.draft !== comment;
        }
        if (mounted.current) setStatus('idle');
      } catch {
        if (mounted.current) setStatus('error');
      } finally {
        current.pending = null;
      }
    });
    return current.pending;
  }, []);

  const change = useCallback(
    (comment: string, meaningful: boolean) => {
      const current = state.current;
      current.draft = comment;
      current.meaningful = meaningful;
      current.dirty = comment !== current.persisted;
      setDraft(comment);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => void flush(), 500);
    },
    [flush]
  );

  useEffect(() => {
    if (!state.current.dirty && !state.current.pending) {
      state.current.draft = initial;
      state.current.persisted = initial;
      state.current.updateId = card.update?.id ?? null;
      setDraft(initial);
    }
  }, [initial, card.update?.id]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      void flush();
    };
  }, [flush]);

  return {draft, status, change, flush};
};
